import { isValid, parse } from 'date-fns'
import isEmpty from 'lodash-es/isEmpty'
import omit from 'lodash-es/omit'

import { DATE_API_DATA_FORMAT_SHORT } from '../../../../../constants/date'
import { PAGE_DEFAULT_VALUE } from '../../../../../constants/route'
import {
  createNowDateInUkTimezone,
  formatDateToApi,
} from '../../../../../utils/date'
import { LISTINGS_MULTISELECT_FILTER_PARAMS } from '../../../../RentalPortalListing'
import {
  LISTINGS_EXTRA_FILTERS,
  LISTINGS_FILTER_TOGGLE_VALUE,
} from '../../RentalPortalListings.constants'

import {
  LISTINGS_BOARD_PAGE_SIZE,
  LISTINGS_DEEP_LINK_DAY_BOUNDARY,
  LISTINGS_FILTER_PARAM_LIST,
  LISTINGS_NUMBER_RANGE_FILTER_PARAMS,
  LISTINGS_TOGGLE_FILTER_PARAMS,
} from './useRentalPortalListingsTablePage.constants'

function getListingsDeepLinkDayBoundary({ param, timezone, value }) {
  const date = parse(
    value,
    DATE_API_DATA_FORMAT_SHORT,
    createNowDateInUkTimezone(timezone)
  )

  return isValid(date)
    ? formatDateToApi(LISTINGS_DEEP_LINK_DAY_BOUNDARY[param](date), timezone)
    : null
}

function getListingsDeepLinkFilterValue({ param, searchParams, timezone }) {
  const value = searchParams.get(param)

  if (LISTINGS_MULTISELECT_FILTER_PARAMS.includes(param)) {
    return searchParams.getAll(param)
  }

  if (LISTINGS_TOGGLE_FILTER_PARAMS.includes(param)) {
    return value === LISTINGS_FILTER_TOGGLE_VALUE ? true : null
  }

  return LISTINGS_DEEP_LINK_DAY_BOUNDARY[param]
    ? getListingsDeepLinkDayBoundary({ param, timezone, value })
    : value
}

function getListingsDeepLinkFilters({ searchParams, timezone }) {
  return Object.fromEntries(
    LISTINGS_FILTER_PARAM_LIST.filter(param => searchParams.has(param))
      .map(param => [
        param,
        getListingsDeepLinkFilterValue({ param, searchParams, timezone }),
      ])
      .filter(([, value]) => value !== null)
  )
}

export function getHasListingsFilterParams(searchParams) {
  return LISTINGS_FILTER_PARAM_LIST.some(param => searchParams.has(param))
}

export function getListingsInitialFilters({ filters, searchParams, timezone }) {
  const deepLinkFilters = getListingsDeepLinkFilters({
    searchParams,
    timezone,
  })

  return isEmpty(deepLinkFilters) ? filters : deepLinkFilters
}

export function getListingsSearchParamsWithoutFilters(searchParams) {
  LISTINGS_FILTER_PARAM_LIST.forEach(param => searchParams.delete(param))

  return searchParams
}

export function getListingsHasActiveFilters(filterValues) {
  return !isEmpty(omit(filterValues, Object.keys(LISTINGS_EXTRA_FILTERS)))
}

export function getListingsBoardListApiParams(params) {
  return {
    ...params,
    page: PAGE_DEFAULT_VALUE,
    size: LISTINGS_BOARD_PAGE_SIZE,
  }
}

export function getListingsBoardPagination(pagination) {
  return {
    ...pagination,
    page: PAGE_DEFAULT_VALUE,
    pageCount: Math.max(
      PAGE_DEFAULT_VALUE,
      Math.ceil(pagination.count / LISTINGS_BOARD_PAGE_SIZE)
    ),
    pageSize: LISTINGS_BOARD_PAGE_SIZE,
  }
}

export function getListingsOrderedRangeFilters(filters) {
  return LISTINGS_NUMBER_RANGE_FILTER_PARAMS.reduce(
    (acc, [startParam, endParam]) =>
      acc?.[startParam] != null &&
      acc[endParam] != null &&
      Number(acc[startParam]) > Number(acc[endParam])
        ? { ...acc, [endParam]: acc[startParam], [startParam]: acc[endParam] }
        : acc,
    filters
  )
}
